
import React, { useState, useEffect } from 'react';
import { Seat, ConferenceConfig, LogicalCoords } from '../types';
import { SeatMap } from './SeatMap';
import { attendeeMatchesQuery, calculateLogicalPositions, getAttendeeFullLabel } from '../services/coreLogic';
import { API } from '../services/api';
import { Spotlight } from './Spotlight';
import * as SecurityService from '../services/security';

interface GuestPanelProps {
  onSwitchToAdmin: (autoAuth: boolean) => void;
}

const STORAGE_KEY = 'smartseat_last_search';

export const GuestPanel: React.FC<GuestPanelProps> = ({ onSwitchToAdmin }) => {
  const [name, setName] = useState('');
  const [viewMode, setViewMode] = useState<'search' | 'map'>('search');
  const [duplicateCandidates, setDuplicateCandidates] = useState<Seat[]>([]);
  
  const [highlightedSeat, setHighlightedSeat] = useState<Seat | null>(null);
  const [showSpotlight, setShowSpotlight] = useState(false);
  
  // New State for loading transition
  const [isNavigating, setIsNavigating] = useState(false);

  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [seats, setSeats] = useState<Seat[]>([]);
  const [config, setConfig] = useState<ConferenceConfig | null>(null);
  const [logicalMap, setLogicalMap] = useState<Map<string, LogicalCoords>>(new Map());
  const [mapCellSize, setMapCellSize] = useState(40);

  // Initialization: Fetch latest data from server
  useEffect(() => {
    async function load() {
        try {
            setIsLoading(true);
            const state = await API.getState();
            setSeats(state.seats || []);
            setConfig(state.config);
        } catch (e) {
            console.error("Failed to load layout from server", e);
        } finally {
            setIsLoading(false);
        }
    }
    load();

    // Load saved name from previous search session
    // 中文注释：历史版本可能把后台密码(admin 开头)误存进 localStorage，这里顺手清掉不再回填。
    const savedName = localStorage.getItem(STORAGE_KEY);
    if (savedName && savedName.toLowerCase().startsWith('admin')) {
        localStorage.removeItem(STORAGE_KEY);
    } else if (savedName) {
        setName(savedName);
    }
  }, []);

  // Sync logicalMap whenever seats or config updates
  useEffect(() => {
    if (seats.length > 0 && config) {
        const newLogicalMap = calculateLogicalPositions(seats, config.rows, config.cols);
        setLogicalMap(newLogicalMap);
    }
  }, [seats, config]);

  // Dynamic cell size calculation for responsive layout
  useEffect(() => {
    const handleResize = () => {
        if (!config) return;
        const width = window.innerWidth * 0.85;
        const calcSize = Math.floor(width / (config.cols || 1));
        setMapCellSize(Math.min(Math.max(calcSize, 30), 80));
    };
    window.addEventListener('resize', handleResize);
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, [config, seats]);

  const handleSearch = async () => {
      const query = name.trim();
      if (!query) return;

      // 中文注释：限制输入长度，避免超长内容被当作密码或姓名送到后端。
      if (query.length > 50) {
          alert('输入内容过长，请确认姓名是否正确。');
          return;
      }

      // 中文注释：约定后台密码一律以 admin 开头。这样只有 admin 开头的输入才会去试登录，
      // 普通与会者查姓名完全不碰登录接口，也就不会再占用/触发后台登录限流。
      const isAdminInput = query.toLowerCase().startsWith('admin');

      if (isAdminInput) {
          // 只保存普通姓名查询，避免把后台密码写进 localStorage。
          try {
              const loginRes = await API.login(query);
              if (loginRes.success) {
                  SecurityService.setSessionToken(loginRes.token);
                  onSwitchToAdmin(true);
                  return;
              }
          } catch (e: any) {
              const errMsg = e.message || '';

              // 中文注释：关键修复——限流(429)必须显式提示并中止，
              // 不能再被静默忽略后掉进“超级管理员恢复模式”，那才是之前死循环的原因。
              if (errMsg.includes('RATE_LIMIT')) {
                  alert('❌ 登录尝试过于频繁，请等待 1 分钟后再试。');
                  return;
              }

              if (
                  errMsg.includes("无法连接") ||
                  errMsg.includes("Connect Error") ||
                  errMsg.includes("Status") ||
                  errMsg.includes("Server Error") ||
                  errMsg.includes("Database") ||
                  errMsg.includes("Environment")
              ) {
                  alert("系统错误: " + errMsg + "\n\n请检查 Cloudflare Workers / D1 部署配置。");
                  return;
              }
          }

          // 中文注释：密码不对时，只有恰好输入 admin 才进入恢复模式(需超级管理员动态码)。
          if (query.toLowerCase() === 'admin') {
              setShowRecoveryModal(true);
              setRecoveryCode('');
              return;
          }

          alert('后台密码错误。');
          return;
      }

      // 普通姓名查询：保存到 localStorage 并在本地已加载的数据里匹配，不产生任何登录请求。
      localStorage.setItem(STORAGE_KEY, query);

      // 修复：输入基础姓名时，也能匹配“王辉（大）/王辉（小）”这类带括号备注的人员。
      const matches = seats.filter(s => s.attendeeName && attendeeMatchesQuery(s.attendeeName, s.attendeeRemark, query));
      
      if (matches.length === 0) {
          alert('未找到该人员，请确认姓名输入是否正确。');
          return;
      }

      if (matches.length === 1) {
          selectSeat(matches[0]);
      } else {
          setDuplicateCandidates(matches);
      }
  };

  const handleRecoveryVerify = async () => {
      // 中文注释：本地先校验 6 位数字，避免无效输入白白消耗限流额度。
      if (!/^\d{6}$/.test(recoveryCode)) {
          alert('请输入 6 位数字动态码。');
          return;
      }
      try {
          await API.resetSystem(recoveryCode);
          setShowRecoveryModal(false);
          alert("✅ 后台密码已重置为 'admin996'\n\n座位与会议配置数据均未改动。");
          const res = await API.login('admin996');
          if (res.success) {
             SecurityService.setSessionToken(res.token);
             onSwitchToAdmin(true);
          }
      } catch (e: any) {
          const errMsg = e.message || '';
          if (errMsg.includes('RATE_LIMIT')) {
              alert('❌ 尝试过于频繁，请等待 1 分钟后再试。');
          } else {
              alert("❌ " + errMsg);
          }
      }
  };

  const selectSeat = (seat: Seat) => {
      setHighlightedSeat(seat);
      setDuplicateCandidates([]);
      setIsNavigating(true);
      setViewMode('map');
      setShowSpotlight(false);

      setTimeout(() => {
          setIsNavigating(false);
          setShowSpotlight(true); 
      }, 700); 
  };

  const handleBackToSearch = () => {
      setViewMode('search');
      setHighlightedSeat(null);
      setShowSpotlight(false);
      setIsNavigating(false);
  };

  const getSeatLabel = (seat: Seat) => {
      const coords = logicalMap.get(seat.id);
      return coords?.isValid ? `${coords.rowLabel}排 ${coords.colLabel}座` : '特殊座位';
  };

  // 修复：前台展示统一使用完整身份标签，方便重名人员确认自己选的是哪一位。
  const getPersonLabel = (seat: Seat) => getAttendeeFullLabel(seat.attendeeName || '', seat.attendeeRemark);

  if (viewMode === 'search') {
      return (
          <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
              <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center border border-gray-100">
                  <h1 className="text-3xl font-extrabold text-primary mb-2">会议座次查询</h1>
                  <p className="text-gray-400 text-sm mb-8 tracking-widest font-medium uppercase">Conference Seating System</p>
                  
                  {isLoading ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <div className="text-gray-400 text-sm animate-pulse">同步服务器数据中...</div>
                      </div>
                  ) : (
                    <div className="relative group">
                        <input 
                            type="text" 
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="w-full px-6 py-4 text-lg bg-slate-50 border-2 border-gray-100 rounded-full focus:border-primary focus:bg-white focus:outline-none transition-all shadow-inner text-gray-900 placeholder-gray-400"
                            placeholder="请输入您的姓名"
                        />
                        <button 
                            onClick={handleSearch} 
                            className="absolute right-2 top-2 bottom-2 bg-primary text-white px-8 rounded-full font-bold hover:bg-blue-600 transition-all active:scale-95 shadow-lg shadow-blue-200"
                        >
                            查询
                        </button>
                    </div>
                  )}
                  
                  <div className="mt-8 text-[10px] text-gray-300 font-mono">
                      Cloud Sync: {isLoading ? 'PENDING' : 'CONNECTED'}
                  </div>
              </div>

              {duplicateCandidates.length > 0 && (
                  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm p-4">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
                          <div className="bg-amber-50 p-5 border-b border-amber-100 flex items-center gap-3">
                              <i className="fa-solid fa-user-group text-amber-600"></i>
                              <div>
                                <h3 className="text-lg font-bold text-amber-900 leading-none">发现重名人员</h3>
                                <p className="text-xs text-amber-700 mt-1">系统中存在多位同名人员，请选择：</p>
                              </div>
                          </div>
                          <div className="p-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                              {duplicateCandidates.map((seat, idx) => (
                                  <button 
                                    key={idx} 
                                    onClick={() => selectSeat(seat)} 
                                    className="w-full text-left p-4 hover:bg-blue-50 transition-colors border-b last:border-0 border-gray-50 flex justify-between items-center group"
                                  >
                                      <div>
                                        <span className="font-bold text-gray-800">{getPersonLabel(seat)}</span>
                                      </div>
                                      <i className="fa-solid fa-chevron-right text-gray-200 group-hover:text-primary transition-colors"></i>
                                  </button>
                              ))}
                          </div>
                          <div className="p-3 bg-gray-50 text-center">
                              <button onClick={() => setDuplicateCandidates([])} className="text-xs text-gray-400 hover:text-gray-600 underline">返回重填</button>
                          </div>
                      </div>
                  </div>
              )}

              {showRecoveryModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] backdrop-blur-sm p-4">
                      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden p-6 border-t-4 border-red-500">
                          <div className="flex items-center gap-3 mb-4">
                            <i className="fa-solid fa-triangle-exclamation text-red-500 text-xl"></i>
                            <h3 className="font-bold text-gray-800">超级管理员恢复模式</h3>
                          </div>
                          <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                            验证超级管理员动态码后，后台密码将恢复为 <span className="font-mono font-bold text-red-600">admin996</span>。
                            <br />座位与会议配置数据不会被修改。
                          </p>
                          <input 
                              type="text" 
                              value={recoveryCode}
                              onChange={(e) => setRecoveryCode(e.target.value)}
                              className="w-full px-4 py-3 border-2 border-red-50 rounded-lg text-2xl tracking-[0.5em] text-center font-mono bg-slate-50 text-red-600 mb-6 focus:border-red-200 focus:outline-none"
                              placeholder="000000"
                              maxLength={6}
                              autoFocus
                          />
                          <div className="flex flex-col gap-2">
                            <button onClick={handleRecoveryVerify} className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-lg shadow-lg shadow-red-100 transition-all active:scale-95">验证并执行重置</button>
                            <button onClick={() => setShowRecoveryModal(false)} className="w-full py-2 text-gray-400 text-xs hover:text-gray-600 transition-colors">取消操作</button>
                          </div>
                      </div>
                  </div>
              )}
          </div>
      );
  }

  return (
      <div className="h-screen bg-slate-50 flex flex-col overflow-hidden relative">
          {isNavigating && (
             <div className="fixed inset-0 z-[100] bg-slate-100/95 backdrop-blur-md flex flex-col items-center justify-center transition-all duration-300">
                 <div className="relative">
                    <div className="w-20 h-20 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <i className="fa-solid fa-location-dot text-primary text-xl animate-bounce"></i>
                    </div>
                 </div>
                 <div className="mt-8 text-primary font-black animate-pulse text-2xl tracking-tight">正在为您定位座次...</div>
             </div>
          )}

          <header className="bg-white shadow-md z-30 px-3 sm:px-6 min-h-[4rem] py-3 flex items-center justify-between relative shrink-0 border-b border-slate-100">
              <button 
                onClick={handleBackToSearch} 
                className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors text-sm font-bold no-print"
              >
                <i className="fa-solid fa-arrow-left"></i> <span>返回</span>
              </button>
              
              {/* 修复：手机端会议标题允许换行，避免长标题被 truncate 截断。 */}
              <h2 className="text-[15px] sm:text-lg md:text-2xl font-black text-gray-800 text-center flex-1 mx-2 sm:mx-4 whitespace-normal break-words leading-snug min-w-0">
                  {config?.title || '会议座次详情'}
              </h2>

              <div className="w-12 no-print"></div>
          </header>

          {highlightedSeat && !isNavigating && (
              <div className="fixed top-[70px] right-3 md:top-24 md:right-8 z-[60] bg-white/95 backdrop-blur-md shadow-2xl rounded-2xl border border-slate-100 p-3 animate-in slide-in-from-right duration-500 max-w-[130px] md:max-w-[160px] text-right">
                   <div className="text-sm md:text-base text-gray-800 font-bold mb-1 break-words leading-tight">
                        {getPersonLabel(highlightedSeat)}
                   </div>
                   
                   <div className="text-lg md:text-xl text-primary font-black leading-none tracking-tighter">
                       {getSeatLabel(highlightedSeat)}
                   </div>

                   <div className="mt-3 pt-2.5 border-t border-slate-100 flex justify-end no-print">
                       <button 
                        onClick={handleBackToSearch} 
                        className="w-full text-xs font-bold bg-primary text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 py-1.5 rounded-lg shadow-sm shadow-blue-100"
                       >
                            <i className="fa-solid fa-rotate-right"></i> 重新查询
                       </button>
                   </div>
              </div>
          )}

          <div className="flex-1 overflow-auto p-4 md:p-10 flex justify-center bg-slate-200/50 relative scroll-smooth custom-scrollbar">
               <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#3b82f6 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

               <div className="w-full flex justify-center items-start z-10">
                   <SeatMap 
                        seats={seats} 
                        rows={config?.rows || 0} 
                        cols={config?.cols || 0} 
                        logicalMap={logicalMap} 
                        readOnly={true} 
                        highlightSeatId={showSpotlight ? highlightedSeat?.id : null} 
                        cellSize={mapCellSize}
                    />
               </div>
          </div>

          {showSpotlight && highlightedSeat && (
            <Spotlight 
                targetId={highlightedSeat.id} 
                onClose={() => setShowSpotlight(false)} 
                title="就坐位置"
                content={getPersonLabel(highlightedSeat)} 
                footer={getSeatLabel(highlightedSeat)}
            />
          )}

          <button 
            onClick={handleBackToSearch}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white text-gray-800 px-6 py-3 rounded-full shadow-2xl border border-gray-100 font-bold text-sm flex items-center gap-2 md:hidden z-40 no-print hover:bg-slate-50 transition-colors"
          >
            <i className="fa-solid fa-magnifying-glass text-primary"></i> 重新查询
          </button>
      </div>
  );
};
